import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import styled from 'styled-components';

// A dual scope and spetrum plot.
// 
// call: ref.current.plot(wave, fftRe, fftIm)


const ScopeInternal = (props, ref) => {
    const { waveformSize, cycleCount } = props;
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const margin = 10;
    const width = waveformSize * cycleCount + 2 * margin + 1;
    const height = Math.round(0.7 * width);

    const drawWave = (ctx, wave) => {
        const left = margin;
        const x0 = Math.round(width / 2);
        const yscale = height / 10;
        const y0 = Math.round(2 * yscale);

        // axes
        ctx.beginPath();
        ctx.moveTo(left, y0);
        ctx.lineTo(width - left, y0);
        ctx.moveTo(x0, y0 - yscale);
        ctx.lineTo(x0, y0 + yscale);
        ctx.strokeStyle = '#4488aa';
        ctx.stroke();

        // wave
        ctx.beginPath();
        ctx.moveTo(left, y0 - yscale * wave[0]);
        const n = wave.length;
        const xscale = (width / 2 - left) / n;
        for (let i = 1; i < 2 * n + 1; i++) {
            ctx.lineTo(left + i * xscale,
                       y0 - yscale * wave[i % n]);
        }
        ctx.strokeStyle = '#333333';
        ctx.stroke();
    };

    const drawFFT = (c, fftRe, fftIm) => {
        const harmonicWidths = [32, 16, 8, 4, 2];
        const harmonicLabels = ['Fundamental', '2nd', '4th', '8th', '16th'];
        const radToDeg = 180.0 / Math.PI;
        const left = 30;
	const dbScale = -4;
	const y0 = height - 200;
	const ylow = y0 - 40 * dbScale;

	// draw dB lines
	c.lineWidth = 1;
	c.strokeStyle = '#888888';
	c.fillStyle = '#888888';
	for (let db = 0; -40 <= db; db -= 10) {
	    const y = y0 + dbScale * db;
	    const t = '' + db + 'dB';
	    c.fillText(t, left - c.measureText(t).width, y + 3);
	    c.beginPath();
	    c.moveTo(left, y);
	    c.lineTo(width - left, y);
	    c.stroke();
	}

	// draw bars
	const octp = 74;
	
	// only the first half matters
	let n = fftRe.length / 2;
	for (let f = 1; f < n; f++) {
	    const l2 = Math.log2(f);
	    const l2i = Math.floor(l2);
	    const x = left + 32 + 1.2 * l2 * octp;

            // abs, log, dB
            const v = fftRe[f] * fftRe[f] + fftIm[f] * fftIm[f];
            const dB = (v < 0.0001) ? -40 : 10 * Math.log10(v);
            // angle, degrees
	    const angle = Math.round(radToDeg * Math.atan2(fftRe[f], fftIm[f]));

	    const y = y0 + dbScale * dB;
	    c.beginPath();    
	    c.moveTo(x, y);
	    c.lineTo(x, ylow);
	    c.strokeStyle = `hsl(${angle},  75%, 40%)`;
	    c.lineWidth = harmonicWidths[l2i];
	    c.stroke();
	    if (0 === (l2 % 1)) {
		const t = harmonicLabels[l2i];
		c.fillText(t, x - 0.5 * c.measureText(t).width, ylow + 10);
	    }
	}
    };

    const plot = (wave, fftRe, fftIm) => {
        if (canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            if (ctx) {
                ctx.clearRect(0, 0, width, height);
                drawFFT(ctx, fftRe, fftIm);
                drawWave(ctx, wave);
            }
        }
    };

    useImperativeHandle(ref,
                        () => ({
                            plot: plot
                        }));
    return (
        <Wrapper>
          <ScopeCanvas width={width} height={height} ref={canvasRef}/>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    display: flex;
    justify-content: center;
`;

const ScopeCanvas = styled.canvas`
    margin: 0 50px;
`;

export const Scope =  forwardRef(ScopeInternal);



